// src/hooks/usePageTracking.ts
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { mixpanelService } from '../lib/mixpanelService';

/**
 * Map of route paths to human-readable page names
 */
const PAGE_NAMES: Record<string, string> = {
  '/': 'Landing',
  '/login': 'Login',
  '/signup': 'Signup',
  '/dashboard': 'Dashboard',
  '/transactions': 'Transactions',
  '/insiders': 'Insiders',
  '/companies': 'Companies',
  '/alerts': 'Alerts',
  '/settings': 'Settings',
  '/upgrade': 'Upgrade',
  '/pricing': 'Pricing',
};

/**
 * Derive a page name from a path that may have dynamic segments
 */
function getPageName(pathname: string): string {
  // Check for exact match first
  if (PAGE_NAMES[pathname]) {
    return PAGE_NAMES[pathname];
  }

  // Check for dynamic routes
  if (pathname.startsWith('/insiders/')) {
    return 'Insider Detail';
  }
  if (pathname.startsWith('/companies/')) {
    return 'Company Detail';
  }
  if (pathname.startsWith('/transactions/')) {
    return 'Transaction Detail';
  }

  // Fallback: capitalize first segment
  const firstSegment = pathname.split('/')[1];
  if (firstSegment) {
    return firstSegment.charAt(0).toUpperCase() + firstSegment.slice(1);
  }

  return 'Unknown';
}

/**
 * Hook to track page views on route changes
 * Call this once at the app level, inside the Router context
 */
export function usePageTracking(): void {
  const location = useLocation();

  useEffect(() => {
    const pageName = getPageName(location.pathname);
    mixpanelService.trackPageView(pageName);
  }, [location.pathname]);
}
